/**
 * Mercenary System Configuration
 *
 * Mercenaries are hireable units stationed at outposts that can:
 * - Form squads to tackle dungeons/encounters automatically
 * - Have random stats (quality roll like equipment)
 * - Can permanently die on failed missions (permadeath)
 */

/**
 * Mercenary class definitions
 * Each class has different stat weights and base stats
 */
export const MERCENARY_CLASSES = {
  warrior: {
    id: 'warrior',
    name: 'Warrior',
    icon: '⚔️',
    description: 'Heavy melee fighter with high HP and defense',
    color: '#c45c5c',  // Red
    statWeights: { STR: 1.5, DEX: 0.8, INT: 0.5, LUK: 0.7 },
    baseStats: { STR: 15, DEX: 10, INT: 8, LUK: 10 },
  },
  ranger: {
    id: 'ranger',
    name: 'Ranger',
    icon: '🏹',
    description: 'Agile ranged attacker with high accuracy',
    color: '#5c9c5c',  // Green
    statWeights: { STR: 0.8, DEX: 1.5, INT: 0.7, LUK: 1.0 },
    baseStats: { STR: 10, DEX: 15, INT: 10, LUK: 12 },
  },
  mage: {
    id: 'mage',
    name: 'Mage',
    icon: '🔮',
    description: 'Powerful spellcaster with high damage but low HP',
    color: '#5c5cc4',  // Blue
    statWeights: { STR: 0.5, DEX: 0.7, INT: 1.5, LUK: 0.8 },
    baseStats: { STR: 8, DEX: 10, INT: 15, LUK: 10 },
  },
  rogue: {
    id: 'rogue',
    name: 'Rogue',
    icon: '🗡️',
    description: 'Swift striker with high crit chance and luck',
    color: '#9c5c9c',  // Purple
    statWeights: { STR: 0.7, DEX: 1.2, INT: 0.6, LUK: 1.5 },
    baseStats: { STR: 10, DEX: 12, INT: 8, LUK: 15 },
  },
};

/**
 * Mercenary hiring costs
 * Cost scales with the mercenary's starting level
 */
export const MERCENARY_HIRING_CONFIG = {
  baseCost: { gold: 500 },
  costPerLevel: { gold: 50 },

  // Starting level range (random)
  startingLevelRange: { min: 1, max: 5 },

  // Quality roll (like equipment quality)
  qualityRange: { min: 0.6, max: 1.4 },

  // Stat points per level
  statPointsPerLevel: 3,

  // XP curve for leveling
  xpToLevel: (level) => Math.floor(100 * Math.pow(1.15, level - 1)),
};

/**
 * Name generation data
 */
export const MERCENARY_NAMES = {
  prefixes: [
    'Sir', 'Lady', 'Master', 'Captain', 'Old', 'Young',
    'Brave', 'Swift', 'Silent', 'Iron', 'Shadow', 'Storm',
  ],
  firstNames: [
    'Aldric', 'Brynn', 'Cedric', 'Dara', 'Eldric', 'Fiona',
    'Gareth', 'Helena', 'Ivar', 'Jana', 'Kael', 'Luna',
    'Marcus', 'Nadia', 'Oren', 'Petra', 'Quinn', 'Rowan',
    'Silas', 'Thea', 'Uther', 'Vera', 'Wren', 'Xavier',
    'Yara', 'Zander', 'Astrid', 'Bjorn', 'Cora', 'Drake',
  ],
  suffixes: [
    'the Bold', 'Shadowblade', 'Ironheart', 'Swiftfoot',
    'the Wise', 'Stormborn', 'Nightwalker', 'Firehand',
    'the Brave', 'Steelarm', 'Quicksilver', 'Darkbane',
    'the Strong', 'Lightbringer', 'Frostborn', 'Goldeneye',
  ],
};

/**
 * Mercenary portrait icons
 */
export const MERCENARY_PORTRAITS = [
  '🧑‍🎤', '🧙', '🧝', '🦹', '💂', '🥷', '🤺', '🧔',
  '👨‍🦰', '👩‍🦰', '👨‍🦳', '👩‍🦳', '🧑‍🦱', '👳', '🤵', '👸',
  '🤴', '🧙‍♂️', '🧙‍♀️', '🧝‍♂️', '🧝‍♀️', '🦸', '🦸‍♂️', '🦸‍♀️',
];

/**
 * Generate a random mercenary name
 * @returns {string} Generated name
 */
export function generateMercenaryName() {
  const { prefixes, firstNames, suffixes } = MERCENARY_NAMES;

  // 30% chance to have a prefix
  const hasPrefix = Math.random() < 0.3;
  // 40% chance to have a suffix
  const hasSuffix = Math.random() < 0.4;

  const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
  const prefix = hasPrefix ? prefixes[Math.floor(Math.random() * prefixes.length)] + ' ' : '';
  const suffix = hasSuffix ? ' ' + suffixes[Math.floor(Math.random() * suffixes.length)] : '';

  return `${prefix}${firstName}${suffix}`;
}

/**
 * Generate random portrait for mercenary
 * @returns {string} Portrait emoji
 */
export function generateMercenaryPortrait() {
  return MERCENARY_PORTRAITS[Math.floor(Math.random() * MERCENARY_PORTRAITS.length)];
}

/**
 * Generate random stats for a new mercenary
 * @param {string} classId - Mercenary class ID
 * @param {number} level - Starting level
 * @returns {{ primaryStats: Object, quality: number }}
 */
export function generateMercenaryStats(classId, level = 1) {
  const mercClass = MERCENARY_CLASSES[classId];
  if (!mercClass) {
    throw new Error(`Unknown mercenary class: ${classId}`);
  }

  const { qualityRange, statPointsPerLevel } = MERCENARY_HIRING_CONFIG;

  // Roll quality (0.6 - 1.4)
  const quality = qualityRange.min + Math.random() * (qualityRange.max - qualityRange.min);

  // Calculate total bonus stat points from level
  const bonusPoints = (level - 1) * statPointsPerLevel;

  // Start with base stats from class
  const primaryStats = { ...mercClass.baseStats };

  // Distribute bonus points weighted by class stat weights
  const weights = mercClass.statWeights;
  const totalWeight = weights.STR + weights.DEX + weights.INT + weights.LUK;

  for (let i = 0; i < bonusPoints; i++) {
    // Weighted random selection of which stat to increase
    const roll = Math.random() * totalWeight;
    let cumulative = 0;

    for (const stat of ['STR', 'DEX', 'INT', 'LUK']) {
      cumulative += weights[stat];
      if (roll <= cumulative) {
        primaryStats[stat]++;
        break;
      }
    }
  }

  return { primaryStats, quality };
}

/**
 * Calculate hiring cost for a mercenary
 * @param {number} level - Mercenary starting level
 * @returns {Object} Resource costs
 */
export function getHiringCost(level) {
  const { baseCost, costPerLevel } = MERCENARY_HIRING_CONFIG;
  const cost = {};

  for (const [resource, amount] of Object.entries(baseCost)) {
    cost[resource] = amount + (costPerLevel[resource] || 0) * (level - 1);
  }

  return cost;
}

/**
 * Get XP needed to reach next level
 * @param {number} currentLevel - Current mercenary level
 * @returns {number} XP needed
 */
export function getMercenaryXpToLevel(currentLevel) {
  return MERCENARY_HIRING_CONFIG.xpToLevel(currentLevel);
}

/**
 * Mercenary status types
 */
export const MERCENARY_STATUS = {
  IDLE: 'idle',           // Available for missions
  MISSION: 'mission',     // Currently on a mission
  RECOVERING: 'recovering', // Recovering from injury (unused for now)
  DEAD: 'dead',           // Permanently dead
};

/**
 * Get quality rating description
 * @param {number} quality - Quality value (0.6 - 1.4)
 * @returns {{ label: string, stars: number, color: string }}
 */
export function getQualityRating(quality) {
  if (quality >= 1.3) {
    return { label: 'Legendary', stars: 5, color: '#fbbf24' }; // Gold
  }
  if (quality >= 1.15) {
    return { label: 'Epic', stars: 4, color: '#a855f7' }; // Purple
  }
  if (quality >= 1.0) {
    return { label: 'Rare', stars: 3, color: '#3b82f6' }; // Blue
  }
  if (quality >= 0.85) {
    return { label: 'Uncommon', stars: 2, color: '#22c55e' }; // Green
  }
  return { label: 'Common', stars: 1, color: '#9ca3af' }; // Gray
}
